import React, { useEffect } from "react";
import { ArrowRight, Percent, Users, Globe2, Ticket, CalendarCheck, ShieldCheck, Phone, Check } from "lucide-react";
import { BRAND } from "../mock";
import Reveal from "../components/Reveal";

const BENEFITS = [
  { icon: Percent, title: "Attraktive Agenturkonditionen", desc: "Netto-Raten und Provisionsmodelle für Reisebüros und Reiseveranstalter." },
  { icon: Users, title: "Gruppen & Sprinter", desc: "Vom Einzeltransfer bis zur Reisegruppe – Vans und Sprinter bis 14 Personen." },
  { icon: Globe2, title: "Mehrsprachige Chauffeure", desc: "Deutsch, Englisch, Französisch, Italienisch – Ihre Gäste fühlen sich willkommen." },
  { icon: Ticket, title: "White-Label Voucher", desc: "Transfers unter Ihrem Namen – mit Voucher und Reiseunterlagen für Ihre Kunden." },
  { icon: CalendarCheck, title: "Zuverlässige Abwicklung", desc: "Flugverfolgung, Meet & Greet und pünktliche Abholung – auf jeder Route." },
  { icon: ShieldCheck, title: "Ein Ansprechpartner", desc: "Feste Kontaktperson für Buchung, Änderungen und Support – rund um die Uhr." },
];

const STEPS = [
  { n: "1", title: "Partner werden", desc: "Registrieren Sie Ihr Reisebüro und erhalten Sie Ihre individuellen Konditionen." },
  { n: "2", title: "Transfers buchen", desc: "Buchen Sie Transfers für Ihre Kunden – einzeln oder für ganze Reisegruppen." },
  { n: "3", title: "Provision sichern", desc: "Profitieren Sie von Netto-Raten oder Provision auf jede vermittelte Fahrt." },
];

const Reisebuero = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="pt-[68px] min-h-screen bg-white overflow-x-hidden" data-testid="reisebuero-page">
      {/* Hero */}
      <div className="relative bg-[#0B1533] text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1527668752968-14dc70a27c95?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/85 to-[#0B1533]/65" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">Reisebüros & Reiseveranstalter</p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Ihr Transfer-Partner für Reisebüros.</h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-6 max-w-2xl leading-relaxed">Bieten Sie Ihren Kunden erstklassige Flughafentransfers in der ganzen Schweiz – mit attraktiven Agenturkonditionen, White-Label Vouchers und zuverlässiger Abwicklung.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href={BRAND.emailHref} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors" data-testid="reise-partner-btn">Partner werden <ArrowRight size={16} /></a>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-white/10 transition-colors"><Phone size={16} /> {BRAND.phone}</a>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal><h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[#0B1533] max-w-2xl">Ihre Vorteile als Reisebüro-Partner.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 60}>
              <div className="h-full rounded-3xl border border-[#E9E5DE] bg-white p-6 hover:border-[#2B4BFF] hover:elev-soft transition-all">
                <span className="inline-flex w-11 h-11 rounded-2xl bg-[#EEF1FF] items-center justify-center mb-4"><b.icon size={20} className="text-[#2B4BFF]" /></span>
                <h3 className="font-heading font-semibold text-lg text-[#0B1533]">{b.title}</h3>
                <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="bg-[#F5F2ED] border-y border-[#E9E5DE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Reveal><h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[#0B1533]">In 3 Schritten zum Partner.</h2></Reveal>
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="rounded-3xl bg-white border border-[#E9E5DE] p-7 h-full">
                  <span className="inline-flex w-10 h-10 rounded-full bg-[#2B4BFF] text-white font-heading font-bold items-center justify-center">{s.n}</span>
                  <h3 className="font-heading font-semibold text-lg text-[#0B1533] mt-4">{s.title}</h3>
                  <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-[28px] bg-[#0B1533] p-8 sm:p-12 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading font-bold text-2xl sm:text-3xl">Werden Sie Reisebüro-Partner.</h3>
            <p className="font-body text-white/60 mt-2 max-w-xl">Kontaktieren Sie uns für Ihre individuellen Konditionen – wir freuen uns auf die Zusammenarbeit.</p>
            <ul className="mt-5 space-y-2">
              {["Netto-Raten & Provision", "White-Label Voucher", "Gruppen bis 14 Personen"].map((f) => (
                <li key={f} className="flex items-center gap-2 font-body text-sm text-white/80"><Check size={16} className="text-[#25D366]" /> {f}</li>
              ))}
            </ul>
          </div>
          <a href={BRAND.emailHref} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#1E38D8] transition-colors shrink-0">Partner werden <ArrowRight size={16} /></a>
        </div>
      </div>
    </div>
  );
};

export default Reisebuero;
